import { selectRxFrequencyLabel } from "@/utils/rxLabelUtils";

const DAY_SHORT = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

const MONTHS_LONG = {
  sn: [
    "Ndira",
    "Kukadzi",
    "Kurume",
    "Kubvumbi",
    "Chivabvu",
    "Chikumi",
    "Chikunguru",
    "Nyamavhuvhu",
    "Gunyana",
    "Gumiguru",
    "Mbudzi",
    "Zvita",
  ],
  zu: [
    "Januwari",
    "Febhuwari",
    "Mashi",
    "Ephreli",
    "Meyi",
    "Juni",
    "Julayi",
    "Agasti",
    "Septhemba",
    "Okthoba",
    "Novemba",
    "Disemba",
  ],
};

function getTranslator(t) {
  return typeof t === "function" ? t : (_key, fallback = "", values = undefined) => {
    if (!values || typeof values !== "object") return fallback;
    return String(fallback || "").replace(/\{(\w+)\}/g, (_, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : `{${key}}`
    );
  };
}

function resolveLocale(languageCode = "") {
  const code = String(languageCode || "").trim().toLowerCase();
  if (code === "sn") return "sn-ZW";
  if (code === "zu") return "zu-ZA";
  return languageCode || undefined;
}

function localizeDoseUnitLabel(rawLabel = "", t) {
  const normalized = String(rawLabel || "").trim().toLowerCase();
  if (!normalized) return "";

  const singular = normalized.replace(/s$/, "");
  const unitKeyByLabel = {
    capsule: "capsule",
    tablet: "tablet",
    "injection solution": "injectionSolution",
    "oral solution": "oralSolution",
    topical: "topical",
    device: "device",
    drops: "drops",
    inhaler: "inhaler",
  };
  const unitKey = unitKeyByLabel[singular];
  if (!unitKey) return rawLabel;
  return t(`prescriptions.wizard.medType.${unitKey}`, rawLabel);
}

function normalizeDays(input, t = undefined) {
  if (!Array.isArray(input)) return [];
  const translate = getTranslator(t);
  return input
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean)
    .map((value) => {
      const key = value.slice(0, 3);
      return translate(`prescriptions.wizard.days.${key}`, DAY_SHORT[key] || value);
    });
}

function parsePositiveNumber(value) {
  const text = String(value ?? "").trim().replace(/,/g, "");
  if (!text) return Number.NaN;
  const numeric = Number(text);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : Number.NaN;
}

function formatDoseAmount(value) {
  if (!Number.isFinite(value)) return "";
  return Number(String(value.toFixed(3))).toString();
}

function formatStrengthDoseLabel(source = {}) {
  const dosesPerTime = parsePositiveNumber(
    source.dosesPerTime ?? source.schedule?.dosesPerTime ?? source.meta?.dosesPerTime
  );

  const strengthValue = parsePositiveNumber(
    source.strengthValue ?? source.medication?.strengthValue ?? source.meta?.strengthValue
  );
  const strengthUnit = String(
    source.strengthUnit ?? source.medication?.strengthUnit ?? source.meta?.strengthUnit ?? ""
  ).trim();

  if (Number.isFinite(strengthValue) && Number.isFinite(dosesPerTime) && strengthUnit) {
    return `${formatDoseAmount(strengthValue * dosesPerTime)} ${strengthUnit}`.trim();
  }

  const strengthLabel = String(
    source.strengthLabel ?? source.medication?.strengthLabel ?? source.meta?.strengthLabel ?? ""
  ).trim();
  const parsedStrength = strengthLabel.match(/^([0-9]+(?:\.[0-9]+)?)\s*([^\d].+)$/);

  if (parsedStrength && Number.isFinite(dosesPerTime)) {
    const [, value, unit] = parsedStrength;
    const baseStrength = parsePositiveNumber(value);
    const normalizedUnit = String(unit || "").trim();
    if (Number.isFinite(baseStrength) && normalizedUnit) {
      return `${formatDoseAmount(baseStrength * dosesPerTime)} ${normalizedUnit}`.trim();
    }
  }

  return "";
}

function formatDoseLabel(source = {}, options = {}) {
  const t = getTranslator(options.t);
  const frequencyType = String(
    source.schedule?.frequencyType || source.frequencyType || ""
  ).trim().toLowerCase();

  const dosesPerTime = String(
    source.dosesPerTime ?? source.schedule?.dosesPerTime ?? ""
  ).trim();
  const doseUnitLabel = String(
    source.doseUnitLabel ?? source.schedule?.doseUnitLabel ?? ""
  ).trim();

  if (!dosesPerTime || !doseUnitLabel) return "";

  const localizedUnit = localizeDoseUnitLabel(doseUnitLabel, t);
  if (frequencyType === "daily") {
    return `${dosesPerTime} ${String(localizedUnit || doseUnitLabel).toLowerCase()} ${t(
      "prescriptions.schedule.dailyDose",
      "{daily}",
      {
        daily: String(t("prescriptions.wizard.frequency.daily", "Daily")).toLowerCase(),
      },
    )}`.trim();
  }

  if (frequencyType === "as_needed") {
    return `${dosesPerTime} ${String(localizedUnit || doseUnitLabel).toLowerCase()} ${t(
      "prescriptions.schedule.asNeededDose",
      "{asNeeded}",
      {
        asNeeded: String(t("prescriptions.wizard.frequency.asNeeded", "As needed")).toLowerCase(),
      },
    )}`.trim();
  }

  return `${dosesPerTime} ${localizedUnit || doseUnitLabel}`.trim();
}

export function buildPrescriptionDosageLabel(source = {}, options = {}) {
  const languageCode = String(options.languageCode || "").trim().toLowerCase();
  const explicitLabel = String(source.dosageLabel || source.dosage || "").trim();
  if (explicitLabel && explicitLabel !== "—" && (!languageCode || languageCode.startsWith("en"))) {
    return explicitLabel;
  }

  const generated =
    formatStrengthDoseLabel(source) || formatDoseLabel(source, options);
  return generated || explicitLabel;
}

function formatScheduleDate(prefixLabel, value, options = {}) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const languageCode = String(options.languageCode || "").trim().toLowerCase();
  const customMonths = MONTHS_LONG[languageCode];
  const formatted = customMonths
    ? `${customMonths[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
    : new Intl.DateTimeFormat(resolveLocale(languageCode), {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date);

  return `${prefixLabel} ${formatted}`;
}

export function buildPrescriptionScheduleRows(source = {}, options = {}) {
  const schedule = source.schedule || {};
  const rows = [];
  const t = getTranslator(options.t);

  const frequencyType = String(
    schedule.frequencyType || source.frequencyType || ""
  ).trim().toLowerCase();

  if (frequencyType === "daily") {
    rows.push(t("prescriptions.wizard.frequency.daily", "Daily"));
    const everyHours = Number(schedule.everyHours || source.everyHours || 0);
    if (everyHours > 0) {
      rows.push(
        `${t("prescriptions.wizard.every", "Every")} ${t(
          "prescriptions.wizard.hoursValue",
          "{count} hours",
          { count: everyHours },
        )}`
      );
    }
  } else if (frequencyType === "specific_days") {
    const days = normalizeDays(schedule.specificDays || source.specificDays, t);
    if (days.length) rows.push(days.join(", "));
  } else if (frequencyType === "cyclical") {
    rows.push(t("prescriptions.wizard.frequency.cyclical", "Cyclical schedule"));
    const cycleUseDays = Number(schedule.cycleUseDays || source.cycleUseDays || 0);
    const cyclePauseDays = Number(
      schedule.cyclePauseDays || source.cyclePauseDays || 0
    );
    if (cycleUseDays > 0) {
      rows.push(
        `${t("prescriptions.wizard.useFor", "Use for")} ${cycleUseDays} ${t(
          "prescriptions.wizard.day",
          "day",
        )}, ${t("prescriptions.wizard.pauseFor", "Pause for")} ${cyclePauseDays} ${t(
          "prescriptions.wizard.day",
          "day",
        )}`
      );
    }
  } else if (frequencyType === "as_needed") {
    rows.push(t("prescriptions.wizard.frequency.asNeeded", "As needed"));
  } else {
    const fallbackLabel = String(
      source.frequencyLabel ||
        source.frequency ||
        selectRxFrequencyLabel({ schedule, t }) ||
        ""
    ).trim();

    if (fallbackLabel && fallbackLabel !== "—") {
      rows.push(fallbackLabel);
    }
  }

  const doseLabel = buildPrescriptionDosageLabel(source, options);
  if (doseLabel) rows.push(doseLabel);

  const startsOn =
    source.startsOn ||
    source.startDateIso ||
    source.meta?.startsOn ||
    schedule.startDateIso ||
    "";
  const endsOn =
    source.endsOn ||
    source.endDateIso ||
    source.meta?.endsOn ||
    schedule.endDateIso ||
    "";

  const startsLabel = formatScheduleDate(
    t("prescriptions.wizard.starts", "Starts"),
    startsOn,
    options,
  );
  const endsLabel = formatScheduleDate(
    t("prescriptions.wizard.ends", "Ends"),
    endsOn,
    options,
  );

  if (startsLabel) rows.push(startsLabel);
  if (endsLabel) rows.push(endsLabel);

  return rows.filter(Boolean);
}
